import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { InlineAppointmentCTA } from "@/components/site/InlineAppointmentCTA";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { medicalWebPageScript, PROCEDURE_ACTORS, SETTING_OUTPATIENT } from "@/lib/medical-schema";

const BALLOON_URL = "https://virginiabariatric.com/services/balloon";

const BALLOON_FAQS: Faq[] = [
  {
    q: "Is the Orbera balloon surgery?",
    a: "No. The balloon is placed and removed endoscopically through the mouth under light sedation. There are no incisions and no permanent change to your anatomy.",
  },
  {
    q: "How long does the gastric balloon stay in?",
    a: "About six months, after which it must be removed endoscopically. The balloon is paired with a structured 12-month nutrition and lifestyle program.",
  },
  {
    q: "How much weight can I expect to lose with a gastric balloon?",
    a: "Most patients lose a meaningful portion of their excess weight during the six months the balloon is in place, but results depend on following the accompanying nutrition and activity program. The balloon produces less weight loss than gastric sleeve or bypass surgery.",
  },
  {
    q: "What are the side effects of the gastric balloon?",
    a: "Nausea, vomiting, and stomach discomfort are common in the first days after placement and usually settle quickly. Less common risks include acid reflux, gastric ulcers, and rarely balloon deflation or migration. A minority of patients need the balloon removed early.",
  },
  {
    q: "Will I regain weight after the balloon is removed?",
    a: "Weight regain is the norm if lifestyle changes are not maintained. The balloon is a temporary tool, not a cure, which is why the 12-month coaching program matters as much as the device.",
  },
  {
    q: "Is Orbera covered by insurance?",
    a: "Insurance rarely covers intragastric balloons, so it is typically a cash-pay procedure. Our office reviews current pricing and financing options with you at consultation.",
  },
];

export const Route = createFileRoute("/services/balloon")({

  head: () => ({
    meta: [
      { title: "Orbera® Gastric Balloon in Northern Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Orbera® is a soft silicone balloon placed in the stomach for up to six months to support short-term weight loss. Placed endoscopically by Dr. Matthew Fitzer.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Orbera® Gastric Balloon in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: "Orbera® is a soft silicone balloon placed in the stomach for up to six months to support short-term weight loss. Placed endoscopically by Dr. Matthew Fitzer." },
        { property: "og:url", content: "https://virginiabariatric.com/services/balloon" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Orbera® intragastric balloon at Virginia Bariatric Surgery" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Orbera® intragastric balloon at Virginia Bariatric Surgery" },
        { name: "twitter:title", content: "Orbera® Gastric Balloon in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "Orbera® is a soft silicone balloon placed in the stomach for up to six months to support short-term weight loss. Placed endoscopically by Dr. Matthew Fitzer." },
      ] : []),
    ],
    links: [
      ...(import.meta.env.SSR ? canonicalAndAlternates("/services/balloon") : []),
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/services/balloon"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": `${BALLOON_URL}#procedure`,
          mainEntityOfPage: { "@id": `${BALLOON_URL}#webpage` },
          name: 'Orbera® Gastric Balloon',
          description: "A temporary, endoscopic weight-loss device that helps patients feel full on less food.",
          url: "https://virginiabariatric.com/services/balloon",
          bodyLocation: "Stomach",
          howPerformed: "A soft silicone balloon is placed into the stomach endoscopically through the mouth under light sedation, then filled with saline. It remains for about six months to limit food volume and promote satiety, and is removed endoscopically. " + SETTING_OUTPATIENT,
          procedureType: "https://schema.org/NoninvasiveProcedure",
          preparation: "Preoperative evaluation, nutrition counseling, and a short clear-liquid diet before placement.",
          followup: "Twelve months of dietitian and medical follow-up, including six months with the balloon in place.",
          ...PROCEDURE_ACTORS,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(BALLOON_FAQS, BALLOON_URL)),
      },
    ] : [],

  }),
  component: BalloonPage,
});

function BalloonPage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif text-center">Orbera® Gastric Balloon</h1>
        <p className="mt-1 text-foreground/70 italic">A non-surgical, temporary tool to jump-start weight loss</p>
      </header>

      <p className="mt-3 text-foreground/80">
        Orbera® is a non-surgical weight-loss option for adults who need help getting started.<sup className="text-[0.7em]">1</sup> It is a soft, durable silicone balloon that is placed in the stomach through a brief outpatient endoscopy. It is done through the mouth, so there are no incisions, no cutting, and no changes to your anatomy. </p>

      <TelehealthCta />

      <p className="mt-3 text-foreground/80">
        Once inside, the balloon is filled with sterile saline until it’s roughly the size of a grapefruit. With the balloon taking up space in the stomach, you feel full sooner and eat smaller portions. The balloon stays in place for six months and then it has to be removed. The device is only half of the treatment: the intragastric balloon system pairs it with a structured, 12-month weight loss program of nutrition coaching, supervised diet changes, and a behavior modification program built for long term weight loss.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">~11–13%</p>
          <p className="mt-1 text-sm text-foreground/80">average total body weight loss over 6 months</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">&lt; 5%</p>
          <p className="mt-1 text-sm text-foreground/80">rate of serious adverse events</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-4 text-center">
          <p className="text-[1.5rem] font-serif font-semibold text-brand-logo">No surgery</p>
          <p className="mt-1 text-sm text-foreground/80">placed and removed by endoscopy, no incisions</p>
        </div>
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">How the Gastric Balloon Works</h2>
      <p className="mt-3 text-foreground/80">
        Balloon placement is performed under light sedation and typically takes about 20 minutes. Using a thin, flexible endoscope passed through the mouth, your physician positions the deflated balloon in the stomach and fills it with saline. (The filling tube is long and of very narrow diameter, so filling is a slow, tedious process.)
      </p>
      <p className="mt-3 text-foreground/80">
        Most patients go home within an hour or two. The balloon then remains in the stomach for six months. At the end of that period, it is deflated and removed during a second (shorter) endoscopic procedure. It is a temporary, fully removable device, which is why nothing is stapled, sutured, or permanently altered.
      </p>

      <InlineAppointmentCTA
        heading="Thinking about a non-surgical start?"
        body="The Orbera gastric balloon can be a good first step for patients who want to jump-start weight loss without surgery. Schedule a consultation to see if you qualify."
        buttonText="Schedule an Appointment to discuss the Orbera gastric balloon"
        secondaryButton={{ text: "Compare Weight-Loss Options", to: "/services" }}
      />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">How Well Does the Balloon Work?</h2>
      <p className="mt-3 text-foreground/80">
        Orbera® is the most widely studied intragastric balloon, and it has a strong evidence base. In the American Society for Gastrointestinal Endoscopy (ASGE) systematic review and meta-analysis—covering 17 studies and 1,683 patients—patients lost an average of 13.2% of their total body weight at 3 months and 11.3% at 12 months, corresponding to about 25% excess weight loss at one year. Across three randomized trials, it produced roughly 27% more excess weight loss than lifestyle changes alone.<sup className="text-[0.7em]">2, 3</sup>
      </p>
      <p className="mt-3 text-foreground/80">
        A larger analysis of 44 studies and more than 5,500 patients found a pooled total body weight loss of 13.2% at six months—the point at which the balloon is removed.<sup className="text-[0.7em]">4</sup>
      </p>
      <p className="mt-3 text-foreground/80">
        In practical terms, a patient weighing 220 pounds might expect to lose in the range of 25 to 30 pounds over the six months the balloon is in place. Because the device is temporary, sustaining that result depends heavily on the supervised diet and habits built during the program. Comparative research also helps set expectations: network meta-analysis of randomized trials shows that fluid-filled balloons like this one produce more weight loss than gas-filled balloons or lifestyle intervention alone, though they may be somewhat less well tolerated in the first days.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Gastric Balloon Benefits</h2>
      <ul className="mt-2 list-disc space-y-1 pl-6 text-foreground/80">
        <li>Non-surgical and incisionless—no cutting, stapling, or permanent change to your stomach</li>
        <li>Fully reversible—the balloon is removed after six months, and anatomy is unaffected</li>
        <li>Same-day, outpatient placement</li>
        <li>A helpful option for patients who do not qualify for, or wish to avoid, bariatric surgery</li>
        <li>A structured 12-month behavior modification program that supports long term weight loss beyond the device</li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Who Is a Candidate for Orbera?</h2>
      <p className="mt-3 text-foreground/80">
        Orbera® is FDA-approved for adults with a body mass index (BMI) between 30 and 40 who have not succeeded with diet and exercise alone. It is often best suited to people who want a non-surgical, lower-commitment starting point, and it can be a good fit for patients who do not meet the <Link to="/resources/qualifications" className="text-brand-deep underline hover:no-underline">bariatric surgery requirements</Link>. It has been used when a patient needs to lose weight to make another procedure feasible. As with any weight-loss intervention, suitability depends on the full medical picture.
      </p>
      <p className="mt-3 text-foreground/80">
        It is not appropriate for patients who have had prior stomach or esophageal surgery, who have a large hiatal hernia, active ulcers or certain gastrointestinal conditions, or who are pregnant. A thorough evaluation by Dr. Fitzer is needed to determine whether it is the right choice.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">What to Expect After Balloon Placement</h2>
      <p className="mt-3 text-foreground/80">
        It is normal to experience nausea, vomiting, and abdominal cramping in the first several days as your stomach adjusts to the balloon. These symptoms are pretty universal and are usually controlled with medications Dr. Fitzer prescribes in advance. Most people ease back to normal activities within a few days and transition through a staged diet from liquids to solid foods. </p>

      <p className="mt-3 text-foreground/80">
        It is helpful to look at the six months with the balloon as an opportunity to establish new eating patterns and an exercise routine. Success after removal depends on those habits, so staying engaged with your dietitian and the weight loss program is one of the strongest predictors of keeping the weight off.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Balloon Risks and Considerations</h2>
      <p className="mt-3 text-foreground/80">
        Serious complications are uncommon—occurring in fewer than 5% of patients in pooled analyses—but like anything else, it is not without risk. The most common effects are temporary nausea, vomiting, and stomach discomfort in the early days. A minority of patients cannot tolerate the balloon and need it removed early. </p>

      <p className="mt-3 text-foreground/80">
        Less common risks include acid reflux, gastric ulcers, and, rarely, balloon deflation or migration, which is why adherence to follow-up and to the recommended fill volume matters. As a temporary device, the balloon is not a cure: weight regain is the norm if lifestyle changes are not maintained after removal.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Next Steps</h2>
      <p className="mt-3 text-foreground/80">
        If you are considering a gastric balloon, the first step is a consultation to review your health history, goals, and whether a temporary balloon fits your needs. Patients who need larger, more durable weight loss often consider{" "}
        <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery (sleeve gastrectomy)</Link>{" "}
        as a next step. Our team provides thorough evaluation and long-term support—before, during, and after the balloon—to help you achieve and maintain your results. Contact our office to schedule an appointment. If you are paying for the balloon yourself, our{" "}
        <Link to="/self-pay-bariatric-surgery" className="text-brand-deep underline hover:no-underline">
          self-pay bariatric surgery
        </Link>{" "}
        page lists the published Orbera price and what is included.
      </p>
      <p className="mt-3 text-foreground/80">
        Not using insurance? See the{" "}
        <Link to="/self-pay-bariatric-surgery" hash="orbera-balloon-cost" className="text-brand-deep underline hover:no-underline">
          Orbera balloon cost
        </Link>{" "}
        self-pay: $8,900, which covers the physician fee, the outpatient facility fee at INOVA Fair
        Oaks Hospital, an anesthesia estimate, and your follow-up through balloon removal. Review
        our{" "}
        <Link to="/resources/prosper" className="text-brand-deep underline hover:no-underline">
          monthly payment plans
        </Link>.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Orbera gastric balloon in Northern Virginia</h2>
      <p className="mt-3 text-foreground/80">The balloon is placed endoscopically as an outpatient procedure at INOVA Fair Oaks Hospital in Fairfax, with no incisions and no overnight stay. The six months of follow-up and coaching that go with it take place at our Herndon office or by telehealth.</p>
      <p className="mt-3 text-foreground/80">For another nonsurgical option, see <Link to="/services/esg" className="text-brand-deep underline hover:no-underline">endoscopic sleeve gastroplasty (ESG) in Northern Virginia</Link>. You can also explore <Link to="/medical-weight-loss" className="text-brand-deep underline hover:no-underline">medical weight loss in Northern Virginia</Link> or <Link to="/services/sleeve" className="text-brand-deep underline hover:no-underline">gastric sleeve surgery in Northern Virginia</Link>.</p>

      <ProcedureFaq heading="Orbera® gastric balloon: frequently asked questions" faqs={BALLOON_FAQS} />

      <div className="mt-6 flex flex-wrap justify-center gap-3">

        <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
          Request a balloon consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>

      <div className="mt-4 text-sm text-foreground/70">
        <p className="font-medium">Selected medical references</p>
        <ol className="mt-1 list-decimal space-y-1 pl-5">
          <li>
            The very first was the Garren-Edwards Gastric Bubble, which was approved by the FDA in 1985. It quickly became apparent that the device had problems, and production was discontinued in 1988. The FDA took the unusual step of withdrawing its approval in 1992.
          </li>
          <li>
            ASGE Bariatric Endoscopy Task Force. Systematic review and meta-analysis assessing the ASGE PIVI thresholds for adopting endoscopic bariatric therapies. <em>Gastrointest Endosc</em>. 2015;82:425-38.e5. doi:10.1016/j.gie.2015.03.1964
          </li>
          <li>
            Kumar N, et al. The Influence of the Orbera Intragastric Balloon Filling Volumes on Weight Loss, Tolerability, and Adverse Events: A Systematic Review and Meta-Analysis. <em>Obes Surg</em>. 2017;27:2272-2278. doi:10.1007/s11695-017-2636-3
          </li>
          <li>
            Bazerbachi F, et al. Fluid-Filled Versus Gas-Filled Intragastric Balloons as Obesity Interventions: A Network Meta-analysis of Randomized Trials. <em>Obes Surg</em>. 2018;28:2617-2625. doi:10.1007/s11695-018-3227-7
          </li>
        </ol>
      </div>
      <LastUpdated className="mt-10" />
      </article>
  );
}
